import React, { useEffect, useMemo, useState } from 'react';
import { withStyles } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Container from '@material-ui/core/Container';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';

import DataService from '../../services/DataService';
import { useAuthContext } from '../../services/AuthService';
import SolutionCard from '../Solutions/SolutionCard';
import styles from './styles';

const UserPage = (props) => {
    const { classes, history, match } = props;
    const username = match.params.username;

    const auth = useAuthContext();

    const [profile, setProfile] = useState(null);
    const [solutions, setSolutions] = useState([]);

    const [projectFilter, setProjectFilter] = useState('All');
    const [projectSearchQuery, setProjectSearchQuery] = useState('');
    const [projectSearchResults, setProjectSearchResults] = useState([]);
    const [projectSearchOpen, setProjectSearchOpen] = useState(false);
    const [projectSearchLoading, setProjectSearchLoading] = useState(false);

    useEffect(() => {
        DataService.GetUserProfile(username).then((response) => {
            const user = response?.data || {};
            const displayName = user.full_name || user.username || 'User';
            const initialsMatch = displayName.replace(/[^a-zA-Z- ]/g, '').match(/\b\w/g) || [];
            const initials = initialsMatch.join('').slice(0, 2).toUpperCase() || displayName.slice(0, 2).toUpperCase();

            setProfile({
                ...user,
                displayName,
                initials,
            });
        });
    }, [username]);

    useEffect(() => {
        const fetchSolutions = () => {
            const isOwnProfile = auth?.state?.username === username;
            const request = isOwnProfile ? DataService.GetMySolutions() : DataService.GetUserSolutions(username);

            request.then((response) => {
                setSolutions(response?.data || []);
            });
        };

        fetchSolutions();
    }, [auth?.state?.username, username]);

    const projectNames = useMemo(() => {
        if (!solutions.length) {
            return [];
        }

        const uniqueNames = new Set(
            solutions
                .map((solution) => solution.projectname)
                .filter((name) => Boolean(name))
        );

        return Array.from(uniqueNames).sort((a, b) => a.localeCompare(b));
    }, [solutions]);

    const solutionsByProject = useMemo(() => {
        const result = new Map();
        const relevantSolutions = projectFilter === 'All'
            ? solutions
            : solutions.filter((solution) => solution.projectname === projectFilter);

        relevantSolutions.forEach((solution) => {
            const key = solution.projectname || 'Unassigned';
            if (!result.has(key)) {
                result.set(key, []);
            }
            result.get(key).push(solution);
        });

        return result;
    }, [projectFilter, solutions]);

    const totalSolutions = solutions.length;
    const visibleSolutionCount = Array.from(solutionsByProject.values()).reduce(
        (count, projectSolutions) => count + projectSolutions.length,
        0
    );

    const projectSections = useMemo(() => Array.from(solutionsByProject.entries()), [solutionsByProject]);
    const hasResults = visibleSolutionCount > 0;

    const handleProjectSearchInput = (_, value) => {
        setProjectSearchQuery(value);

        if (!value) {
            setProjectSearchResults([]);
            setProjectSearchLoading(false);
            return;
        }

        setProjectSearchLoading(true);
        DataService.SearchProjects(value)
            .then((response) => {
                const availableNames = new Set(projectNames);
                const options = (response?.data || []).filter((project) => availableNames.has(project.projectname));
                setProjectSearchResults(options);
            })
            .catch(() => {
                setProjectSearchResults([]);
            })
            .finally(() => {
                setProjectSearchLoading(false);
            });
    };

    const handleProjectSelection = (_, value) => {
        if (value?.projectname) {
            setProjectFilter(value.projectname);
        }
    };

    const handleProjectChipClick = (name) => {
        setProjectFilter(name);
    };

    const resetProjectFilter = () => {
        setProjectFilter('All');
    };

    const goToSolution = (solution) => {
        history.push(`/solutions/${solution.id}`);
    };

    return (
        <div className={classes.root}>
            <main className={classes.main}>
                <Container maxWidth="lg">
                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12} md={3}>
                            <Paper className={classes.profileCard} variant="outlined" square>
                                <div className={classes.profileHeader}>
                                    <Avatar
                                        className={classes.avatar}
                                        src={profile?.profile_image_url || undefined}
                                        alt={profile?.displayName || username}
                                    >
                                        {profile?.initials}
                                    </Avatar>
                                    <Typography variant="h5" className={classes.profileName}>
                                        {profile?.displayName || 'User'}
                                    </Typography>
                                    <Typography variant="body2" className={classes.username}>
                                        @{profile?.username || username}
                                    </Typography>
                                </div>

                                <Divider />

                                <div className={classes.profileFields}>
                                    <div className={classes.profileField}>
                                        <Typography className={classes.profileLabel}>
                                            GitHub
                                        </Typography>
                                        <Typography className={classes.profileValue}>
                                            {profile?.github_username || '—'}
                                        </Typography>
                                    </div>
                                    <div className={classes.profileField}>
                                        <Typography className={classes.profileLabel}>
                                            Twitter
                                        </Typography>
                                        <Typography className={classes.profileValue}>
                                            {profile?.twitter_handle || '—'}
                                        </Typography>
                                    </div>
                                    <div className={classes.profileField}>
                                        <Typography className={classes.profileLabel}>
                                            Research Interests
                                        </Typography>
                                        <Typography className={classes.profileValue}>
                                            {profile?.research_interests || '—'}
                                        </Typography>
                                    </div>
                                </div>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={9}>
                            <div className={classes.solutionsColumn}>
                                <Box className={classes.controlCard}>
                                    <Box className={classes.controlRow}>
                                        <Typography variant="h5">
                                            Solutions
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {visibleSolutionCount} of {totalSolutions} shown
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <Box className={classes.controlRow}>
                                        <Autocomplete
                                            disableClearable
                                            open={projectSearchOpen}
                                            onOpen={() => setProjectSearchOpen(true)}
                                            onClose={() => setProjectSearchOpen(false)}
                                            inputValue={projectSearchQuery}
                                            onInputChange={handleProjectSearchInput}
                                            onChange={handleProjectSelection}
                                            options={projectSearchResults}
                                            getOptionLabel={(option) => option.projectname}
                                            loading={projectSearchLoading}
                                            className={classes.projectSearch}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Find project"
                                                    variant="outlined"
                                                    size="small"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        type: 'search',
                                                        endAdornment: (
                                                            <React.Fragment>
                                                                {projectSearchLoading ? (
                                                                    <CircularProgress color="inherit" size={18} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </React.Fragment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Icon>clear_all</Icon>}
                                            onClick={resetProjectFilter}
                                            className={classes.resetButton}
                                        >
                                            Show All
                                        </Button>
                                    </Box>

                                    {projectNames.length > 0 && (
                                        <React.Fragment>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Quick filters
                                            </Typography>
                                            <div className={classes.projectChipGroup}>
                                                <Chip
                                                    label="All Projects"
                                                    clickable
                                                    color={projectFilter === 'All' ? 'primary' : 'default'}
                                                    onClick={resetProjectFilter}
                                                />
                                                {projectNames.map((name) => (
                                                    <Chip
                                                        key={name}
                                                        label={name}
                                                        clickable
                                                        color={projectFilter === name ? 'primary' : 'default'}
                                                        onClick={() => handleProjectChipClick(name)}
                                                    />
                                                ))}
                                            </div>
                                        </React.Fragment>
                                    )}
                                </Box>

                                {hasResults ? (
                                    projectSections.map(([projectName, projectSolutions]) => (
                                        <Box key={projectName} className={classes.projectSection}>
                                            <Box className={classes.controlRow}>
                                                <Typography variant="h6" className={classes.projectHeading}>
                                                    Project: {projectName}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {projectSolutions.length}{' '}
                                                    {projectSolutions.length === 1 ? 'solution' : 'solutions'}
                                                </Typography>
                                            </Box>
                                            <Divider />
                                            <Grid container spacing={2} className={classes.solutionsGrid}>
                                                {projectSolutions.map((solution) => (
                                                    <Grid item xs={12} sm={6} md={4} key={solution.id || solution.name}>
                                                        <SolutionCard
                                                            solution={solution}
                                                            clickHandle={goToSolution}
                                                            showStatus
                                                            showEquation
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    ))
                                ) : (
                                    <Box className={classes.emptyState}>
                                        <Typography variant="subtitle1">
                                            No solutions found
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Create or publish a solution to see it listed here.
                                        </Typography>
                                    </Box>
                                )}
                            </div>
                        </Grid>
                    </Grid>
                </Container>
            </main>
        </div>
    );
};

export default withStyles(styles)(UserPage);
